import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { fetchRisks, updateRisk } from "../../../store/startup/risk/riskSlice";
import {
    Box,
    Typography,
    Button,
    useTheme,
    alpha
} from "@mui/material";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

const Risk = () => {
    const dispatch = useDispatch();
    const { startupId } = useParams();
    const { risksByType, loading } = useSelector(state => state.risk);
    const [risks, setRisks] = useState({
        1: "",
        2: "",
        3: "",
        4: ""
    });
    const [errors, setErrors] = useState({
        1: false,
        2: false,
        3: false,
        4: false
    });
    const theme = useTheme();

    useEffect(() => {
        dispatch(fetchRisks(startupId));
    }, [dispatch, startupId]);

    useEffect(() => {
        setRisks(risksByType);
    }, [risksByType]);

    const handleRiskChange = (type, value) => {
        setRisks(prev => ({
            ...prev,
            [type]: value
        }));
        // Reset error when user starts typing
        setErrors(prev => ({
            ...prev,
            [type]: false
        }));
    };

    const handleSave = async (type) => {
        // Reset specific error
        setErrors(prev => ({
            ...prev,
            [type]: false
        }));

        // Validate input
        if (!risks[type]?.trim()) {
            setErrors(prev => ({
                ...prev,
                [type]: true
            }));
            return;
        }

        try {
            await dispatch(updateRisk({
                startupId,
                riskType: type,
                description: risks[type]
            })).unwrap();
        } catch (error) {
            console.error(`Failed to update risk type ${type}:`, error);
        }
    };

    const riskTitles = {
        1: "Projeye İlişkin Riskler",
        2: "Sektöre İlişkin Riskler",
        3: "Paylara İlişkin Riskler",
        4: "Diğer Riskler"
    };

    const quillModules = {
        toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            ['clean']
        ]
    };

    const quillFormats = [
        'header',
        'bold', 'italic', 'underline', 'strike',
        'list', 'bullet'
    ];

    return (
        <Box sx={{ 
            margin: "0 auto", 
            maxWidth: 900,
            '& .quill': {
                '& .ql-toolbar': {
                    border: `1px solid ${alpha(theme.palette.grey[500], 0.2)}`,
                    borderTopLeftRadius: theme.shape.borderRadius,
                    borderTopRightRadius: theme.shape.borderRadius,
                    backgroundColor: theme.palette.mode === 'light' 
                        ? alpha(theme.palette.grey[100], 0.5)
                        : alpha(theme.palette.grey[800], 0.5),
                    '& .ql-stroke': {
                        stroke: theme.palette.mode === 'light' 
                            ? theme.palette.grey[700]
                            : theme.palette.grey[300]
                    },
                    '& .ql-fill': {
                        fill: theme.palette.mode === 'light' 
                            ? theme.palette.grey[700]
                            : theme.palette.grey[300]
                    },
                    '& .ql-picker': {
                        color: theme.palette.text.primary
                    },
                    '& .ql-picker-options': {
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${alpha(theme.palette.grey[500], 0.2)}`,
                        boxShadow: theme.shadows[3]
                    }
                },
                '& .ql-container': {
                    borderBottomLeftRadius: theme.shape.borderRadius,
                    borderBottomRightRadius: theme.shape.borderRadius,
                    border: `1px solid ${alpha(theme.palette.grey[500], 0.2)}`,
                    borderTop: 0,
                    backgroundColor: theme.palette.mode === 'light' 
                        ? alpha(theme.palette.common.white, 0.8)
                        : alpha(theme.palette.grey[900], 0.8),
                    '& .ql-editor': {
                        minHeight: '150px',
                        color: theme.palette.text.primary,
                        fontSize: '0.875rem',
                        fontFamily: theme.typography.fontFamily,
                        '&.ql-blank::before': {
                            color: theme.palette.text.disabled,
                            fontStyle: 'normal'
                        }
                    }
                }
            }
        }}>
            {Object.entries(riskTitles).map(([type, title]) => (
                <Box key={type} sx={{ mb: 4 }}>
                    <Typography 
                        variant="h6" 
                        gutterBottom
                        sx={{
                            color: theme.palette.mode === 'light' 
                                ? theme.palette.grey[800]
                                : theme.palette.grey[100],
                            fontWeight: 600
                        }}
                    >
                        {title}
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                        <ReactQuill 
                            theme="snow"
                            value={risks[type]}
                            onChange={(value) => handleRiskChange(type, value)}
                            modules={quillModules}
                            formats={quillFormats}
                        />
                        {errors[type] && (
                            <Typography 
                                color="error" 
                                variant="caption" 
                                sx={{ mt: 1, display: 'block' }}
                            >
                                Bu alan boş bırakılamaz
                            </Typography>
                        )}
                    </Box>
                    <Box sx={{ textAlign: "right" }}>
                        <Button 
                            variant="contained"
                            onClick={() => handleSave(type)}
                            disabled={loading || risks[type] === risksByType[type]}
                            size="small"
                            sx={{
                                bgcolor: theme.palette.primary.main,
                                '&:hover': {
                                    bgcolor: theme.palette.primary.dark,
                                },
                                '&:disabled': {
                                    bgcolor: alpha(theme.palette.primary.main, 0.4),
                                }
                            }}
                        >
                            Kaydet
                        </Button>
                    </Box>
                </Box>
            ))}
        </Box>
    );
};

export default Risk;
